'use client';
import ButtonBase from '@mui/material/ButtonBase';
import {styled, type SxProps, type Theme} from '@mui/material/styles';
import {Typography} from './Typography';

export interface PaginationNumberProps {
  page: number;
  selected?: boolean;
  onClick?: () => void;
  'aria-label'?: string;
  sx?: SxProps<Theme>;
  className?: string;
}

// [MASTER] Pagination number — Figma node 2302:21994, 40x40 pill.
const PAGINATION_NUMBER_SIZE = 40;

// Built on ButtonBase for the same reset (inline-flex centering, cursor,
// border/background reset) NavArrow uses, so the two pills line up in
// `[MASTER] Pagination`.
const Root = styled(ButtonBase, {shouldForwardProp: propName => propName !== 'oSelected'})<{oSelected: boolean}>(({theme, oSelected}) => {
  const iconStrong = theme.palette.colors.icon.neutral.strong;
  return {
    flexShrink: 0,
    width: PAGINATION_NUMBER_SIZE,
    height: PAGINATION_NUMBER_SIZE,
    borderRadius: theme.radii.full,
    border: oSelected ? 'none' : `1px solid ${theme.palette.colors.border.neutral.subtle}`,
    backgroundColor: oSelected ? theme.palette.colors.surface.primary.default : theme.palette.colors.surface.neutral.default,
    color: oSelected ? theme.palette.colors.text.primary.onColor : iconStrong,
    '&:hover:not(:disabled)': {
      backgroundColor: oSelected ? theme.palette.colors.surface.primary.hover : theme.palette.colors.surface.neutral.hover,
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.colors.border.focus.default}`,
      outlineOffset: 2,
    },
  };
});

export function PaginationNumber({page, selected = false, sx, ...rest}: PaginationNumberProps) {
  return (
    <Root type="button" oSelected={selected} aria-current={selected ? 'page' : undefined} sx={sx} {...rest}>
      <Typography
        size="body-2"
        weight="medium"
        as="span"
        sx={{color: 'inherit', letterSpacing: '-0.015em', textBox: 'trim-both cap alphabetic'}}
      >
        {page}
      </Typography>
    </Root>
  );
}

PaginationNumber.displayName = 'PaginationNumber';
